/**
 * The **reconnect/backoff policy** shared by both client sockets (`src/main.ts`):
 * the login screen's read-only spectator backdrop, and - since ADR-0111 - the real
 * gameplay connection after the splash is submitted.
 *
 * Born for the backdrop (ADR-0069): a single *transient* drop of the login
 * screen's live-world preview (the server is reachable but momentarily sheds the
 * one read-only preview connection - most likely under the single-VM concurrency
 * ceiling, #117/#119) must **not** strand a visitor who could still log in, so the
 * shell reconnects with exponential backoff rather than immediately flipping to
 * the blocking error/retry. The gameplay socket reuses the identical decision with
 * its own tunables (ADR-0111): a drop mid-session shows the "Reconnecting…"
 * overlay and re-runs the login handshake on backoff, so a server redeploy or
 * network blip self-heals. In both cases only a *sustained* outage - staying down
 * past the elapsed budget - escalates to the blocking error state.
 *
 * This module is the **pure decision** (the codebase idiom - cf. {@link HudClock},
 * {@link SoundObserver}, `mouseMove`): given how many closes have been seen and how
 * long the outage has run, it returns the next backoff delay and whether to keep
 * retrying or escalate. `main.ts` is the thin imperative shell that wires the real
 * `WebSocket`s to this decision. It reads no socket, no DOM, no clock - the elapsed
 * time is passed in - so it is unit-tested directly.
 */

/** Tunables for {@link planReconnect}; passed in so the policy stays pure. */
export interface ReconnectPolicy {
  /** Delay before the first reconnect (ms); each further attempt doubles it. */
  readonly baseDelayMs: number;
  /** Hard ceiling on any single backoff delay (ms), so the wait never runs away. */
  readonly maxDelayMs: number;
  /**
   * Elapsed-outage budget (ms): once the socket has stayed down this long - or the
   * next backoff wait would overrun it - stop retrying and escalate to error/retry.
   * The backdrop pins this to `main.ts`'s no-snapshot `LOAD_TIMEOUT_MS` backstop so
   * a real outage surfaces an error rather than retrying silently forever; the
   * gameplay socket runs a longer budget (a live session tolerates a redeploy).
   */
  readonly budgetMs: number;
}

/** What {@link planReconnect} decides for a given close. */
export interface ReconnectPlan {
  /** Keep going (schedule another connect after {@link delayMs}) vs. escalate to error. */
  readonly retry: boolean;
  /** How long to wait before the next attempt (ms); meaningful only when {@link retry}. */
  readonly delayMs: number;
}

/**
 * Decide how to react to a socket close mid-outage. `attempt` is the number of
 * closes seen so far (1 for the first drop); `elapsedMs` is how long the outage
 * has run (since the first drop / connection attempt).
 *
 * The backoff is exponential off {@link ReconnectPolicy.baseDelayMs}, doubling each
 * attempt and clamped to {@link ReconnectPolicy.maxDelayMs} (e.g. 250ms, 500ms, 1s,
 * 2s, 2s, …). It escalates ({@link ReconnectPlan.retry} `false`) once the elapsed
 * outage plus the next wait would reach {@link ReconnectPolicy.budgetMs}, so a
 * transient drop recovers on its own while a sustained outage still surfaces the
 * blocking error/retry within its policy's budget.
 */
export function planReconnect(
  attempt: number,
  elapsedMs: number,
  policy: ReconnectPolicy,
): ReconnectPlan {
  const steps = Math.max(0, attempt - 1);
  const delayMs = Math.min(policy.maxDelayMs, policy.baseDelayMs * 2 ** steps);
  const retry = elapsedMs + delayMs < policy.budgetMs;
  return { retry, delayMs };
}
